import { Box, type BoxProps, type SxProps, type Theme } from "@mui/material";

// Imagem própria (fora do MUI) — folha de cannabis em verde.
import cannabisLeafImg from "@/assets/cannabis-leaf.png";

type CannabisIconProps = {
  /** Compatível com ícones MUI (fontSize numérico ou "small" | "medium" | "large"). */
  fontSize?: number | "inherit" | "small" | "medium" | "large";
  sx?: SxProps<Theme>;
  className?: string;
  /** Mantido para API parecida com SvgIcon; a imagem já é verde fixa. */
  color?: string;
  "aria-hidden"?: boolean | "true" | "false";
};

const SIZE_MAP = {
  inherit: "1em",
  small: 20,
  medium: 24,
  large: 32,
} as const;

function resolveSize(
  fontSize: CannabisIconProps["fontSize"],
  sx?: SxProps<Theme>,
): number | string {
  // PageHeader / menus passam tamanho via sx.fontSize
  const sxObj = (Array.isArray(sx) ? Object.assign({}, ...sx) : sx) as
    | BoxProps["sx"]
    | undefined;
  const fromSx =
    sxObj && typeof sxObj === "object" && !Array.isArray(sxObj)
      ? (sxObj as { fontSize?: number | string }).fontSize
      : undefined;

  if (typeof fromSx === "number" || typeof fromSx === "string") return fromSx;
  if (typeof fontSize === "number") return fontSize;
  if (fontSize && fontSize in SIZE_MAP) {
    return SIZE_MAP[fontSize as keyof typeof SIZE_MAP];
  }
  return SIZE_MAP.medium;
}

/**
 * Ícone de folha de cannabis (PNG verde próprio, fora do MUI Icons).
 * Usado no atalho e na tela de tratamento canabinoide.
 */
export function CannabisIcon({
  fontSize = "medium",
  sx,
  className,
  "aria-hidden": ariaHidden = true,
}: CannabisIconProps) {
  const size = resolveSize(fontSize, sx);

  return (
    <Box
      component="img"
      src={cannabisLeafImg}
      alt=""
      aria-hidden={ariaHidden}
      className={className}
      sx={{
        width: size,
        height: size,
        display: "inline-block",
        objectFit: "contain",
        flexShrink: 0,
        verticalAlign: "middle",
        // Ignora color do tema — a folha já é verde na imagem
        color: "unset",
        ...(sx as object),
      }}
    />
  );
}

export default CannabisIcon;
